import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import KpiModuleCard from '../charts/KpiModuleCard';
import TrendKpiCard from '../charts/TrendKpiCard';
import GaugeChart from '../charts/GaugeChart';
import MiniBarChart from '../charts/MiniBarChart';
import {
  calcAvgResolutionDays,
  calcOverdueCount,
  calcTopCategories,
  calcResolutionByPeriod,
  calcResolutionTrend,
} from '../kpiService';
import type { KpiDashboardConfig } from '../kpiConfig';

interface Props {
  observations: any[];
  config: KpiDashboardConfig;
  stretch?: boolean;
}

// Arc fill is % of open actions that are overdue; color zones reflect severity
const OVERDUE_ZONES = [
  { threshold: 10,  color: '#2E7D32' },
  { threshold: 30,  color: '#F57F17' },
  { threshold: 100, color: '#C62828' },
];

export default function ComplianceModule({ observations, config, stretch }: Props) {
  const sndo      = config.sameAndNextDayAsOne;
  const avgDays    = useMemo(() => calcAvgResolutionDays(observations, sndo), [observations, sndo]);
  const overdue    = useMemo(() => calcOverdueCount(observations), [observations]);
  const topCats    = useMemo(() => calcTopCategories(observations), [observations]);
  const periodBars = useMemo(() => calcResolutionByPeriod(observations, sndo), [observations, sndo]);
  const trend      = useMemo(() => calcResolutionTrend(observations, sndo), [observations, sndo]);

  const obs_        = Array.isArray(observations) ? observations : [];
  const openCount   = obs_.filter(i => i.Status !== 'Closed').length;
  const closedCount = obs_.filter(i => i.Status === 'Closed').length;
  const maxCatVal   = topCats.length > 0 ? topCats[0].value : 1;

  return (
    <KpiModuleCard title="Compliance & Action Resolution KPIs" accentColor="#1C60A5" stretch={stretch}>
      <View style={styles.row}>
        {/* Resolution Time */}
        <TrendKpiCard
          label="Observation Resolution Time"
          value={avgDays.toFixed(1)}
          unit="Days (Avg)"
          accent="#1C60A5"
          subtitle={`${closedCount} closed  |  ${openCount} open`}
          lowerIsBetter
          delta={trend?.changePct}
          deltaLabel={trend ? `from ${trend.prevPeriodLabel}` : undefined}
        >
          {periodBars.length > 0 && (
            <MiniBarChart data={periodBars} barColor="#1C60A5" height={55} />
          )}
        </TrendKpiCard>

        {/* Overdue Actions Gauge */}
        <View style={styles.gaugeBlock}>
          <Text style={styles.gaugeLabel}>OVERDUE ACTIONS</Text>
          <GaugeChart
            value={overdue.fillPct}
            size={110}
            centerText={String(overdue.overdueCount)}
            colorZones={OVERDUE_ZONES}
          />
          <Text style={styles.gaugeSub}>
            {overdue.overdueCount} of {overdue.openCount} open actions
          </Text>
        </View>

        {/* Top Observation Categories */}
        <View style={styles.catsBlock}>
          <Text style={styles.blockLabel}>TOP OBSERVATION CATEGORIES</Text>
          {topCats.length === 0 ? (
            <Text style={styles.gaugeSub}>No data</Text>
          ) : topCats.map((cat, i) => {
            const pct = Math.round((cat.value / maxCatVal) * 100);
            return (
              <View key={i} style={styles.catRow}>
                <Text style={styles.catLabel} numberOfLines={1}>{cat.label}</Text>
                <View style={styles.catTrack}>
                  <View style={[styles.catFill, { width: `${pct}%` as any }]} />
                </View>
                <Text style={styles.catCount}>{cat.value}</Text>
              </View>
            );
          })}
        </View>
      </View>
    </KpiModuleCard>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  gaugeBlock: {
    flex: 1,
    minWidth: 150,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginVertical: 4,
  },
  gaugeLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#777',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
    textAlign: 'center',
  },
  gaugeSub: {
    fontSize: 10,
    color: '#999',
    marginTop: 4,
    textAlign: 'center',
  },
  catsBlock: {
    flex: 2,
    minWidth: 200,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginVertical: 4,
    justifyContent: 'center',
  },
  blockLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#777',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 10,
  },
  catRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 7,
    gap: 6,
  },
  catLabel: {
    width: 110,
    fontSize: 10,
    color: '#555',
    fontWeight: '500',
  },
  catTrack: {
    flex: 1,
    height: 6,
    backgroundColor: '#E0E0E0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  catFill: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#1C60A5',
  },
  catCount: {
    width: 24,
    fontSize: 10,
    fontWeight: '700',
    color: '#333',
    textAlign: 'right',
  },
});
